import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, TrendingUp, DollarSign, ShoppingBag, BarChart3, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PRO_PRICE_LABEL } from '../pricing';

const MESSAGES = [
  {
    icon: DollarSign,
    headline: 'Your insights are waiting',
    body: (name: string) =>
      `${name}, for ${PRO_PRICE_LABEL} a month you get a morning brief that can save you hours of spreadsheet work every week.`,
  },
  {
    icon: TrendingUp,
    headline: 'Don\'t fly blind',
    body: (name: string) =>
      `${name}, ${PRO_PRICE_LABEL}/month is less than one wasted inventory order. ClearPath tells you exactly what's selling and what's dying.`,
  },
  {
    icon: ShoppingBag,
    headline: 'Your data is still here',
    body: (name: string) =>
      `${name}, all your uploaded data is safe. Reactivate Pro to instantly see your dashboard, product scores, and AI recommendations again.`,
  },
  {
    icon: BarChart3,
    headline: 'One decision pays for itself',
    body: (name: string) =>
      `${name}, a single smart bundle or a timely product cut can make back ${PRO_PRICE_LABEL} many times over. Let ClearPath find those opportunities.`,
  },
  {
    icon: Sparkles,
    headline: 'Your competitors aren\'t guessing',
    body: (name: string) =>
      `${name}, while you're flying blind, other businesses are making data-driven decisions every day. Get back in the game for ${PRO_PRICE_LABEL}/month.`,
  },
];

export default function CancelledOverlay({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [messageIndex, setMessageIndex] = useState(0);
  const isCancelled = user?.subscription_status === 'cancelled' && user?.plan === 'pro';

  useEffect(() => {
    setMessageIndex(Math.floor(Math.random() * MESSAGES.length));
  }, []);

  if (!isCancelled) return <>{children}</>;

  const msg = MESSAGES[messageIndex];
  const Icon = msg.icon;
  const firstName = user?.name?.split(' ')[0] || 'there';

  return (
    <div className="relative">
      {/* Blurred content — user can still see their data but can't interact */}
      <div className="blur-[6px] pointer-events-none select-none opacity-60">
        {children}
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 flex items-start justify-center pt-24 z-10">
        <div className="bg-white border border-[#E6E2DE] rounded-[16px] p-8 shadow-xl max-w-md mx-4 text-center">
          <div className="w-14 h-14 rounded-full bg-[#4A2D5E]/10 flex items-center justify-center mx-auto mb-4">
            <Icon size={28} strokeWidth={1.5} className="text-[#4A2D5E]" />
          </div>
          <h2 className="font-serif text-xl text-[#2D2A2E] mb-2">{msg.headline}</h2>
          <p className="font-sans text-sm text-[#6B6670] leading-relaxed mb-6">
            {msg.body(firstName)}
          </p>
          <button
            onClick={() => navigate('/settings')}
            className="w-full bg-[#2E6B4F] text-white hover:bg-[#245A41] rounded-[8px] shadow-sm font-sans font-medium py-3 transition-colors mb-3"
          >
            Reactivate Pro — {PRO_PRICE_LABEL}/mo
          </button>
          <button
            onClick={() => setMessageIndex((messageIndex + 1) % MESSAGES.length)}
            className="font-sans text-xs text-[#6B6670] hover:text-[#4A2D5E] transition-colors"
          >
            Why reactivate? →
          </button>
        </div>
      </div>
    </div>
  );
}
